import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Mascot } from '@/components/Mascot';
import { FeedbackDialog } from '@/components/FeedbackDialog';
import { MessageSquare, X, Heart } from 'lucide-react';

interface FeedbackPromptDialogProps {
  open: boolean;
  onDismiss: () => void;
  onFeedbackGiven: () => void;
}

export function FeedbackPromptDialog({ open, onDismiss, onFeedbackGiven }: FeedbackPromptDialogProps) {
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);

  const handleOpenFeedback = () => {
    setShowFeedbackForm(true);
  };

  const handleFeedbackClose = (submitted: boolean) => {
    setShowFeedbackForm(false);
    if (submitted) {
      onFeedbackGiven();
    }
  };

  if (showFeedbackForm) {
    return (
      <FeedbackDialog 
        open={showFeedbackForm}
        onOpenChange={(open) => {
          if (!open) handleFeedbackClose(false);
        }}
        onSubmitSuccess={() => handleFeedbackClose(true)}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onDismiss()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="relative">
              <Mascot mood="happy" size="lg" />
              <div className="absolute -top-1 -right-1 bg-cinnamon text-white rounded-full p-1">
                <Heart className="h-4 w-4 fill-current" />
              </div>
            </div>
          </div>
          <DialogTitle className="text-xl font-display">
            How's Grounded working for you?
          </DialogTitle>
          <DialogDescription className="text-base">
            We'd love to hear your thoughts! Your feedback helps us build a better experience for remote workers everywhere.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 mt-4">
          <Button 
            onClick={handleOpenFeedback}
            className="w-full gap-2"
            size="lg"
          >
            <MessageSquare className="h-5 w-5" />
            Share Your Feedback
          </Button>
          
          <Button 
            variant="ghost" 
            onClick={onDismiss}
            className="w-full text-muted-foreground"
          >
            <X className="h-4 w-4 mr-2" />
            Maybe Later
          </Button>
        </div>

        <p className="text-xs text-center text-muted-foreground mt-2">
          We won't ask again for a while 😊
        </p>
      </DialogContent>
    </Dialog>
  );
}
